// ProductDetails.jsx
import React, {
  useState, useEffect, useRef, useCallback, useMemo
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShoppingCart, Star, ChevronRight, ChevronLeft,
  Minus, Plus, ArrowRight, Truck, RotateCcw, Shield, Award,
  Headphones, Heart, ZoomIn, ZoomOut, X, Eye,
  Sparkles, ArrowLeft, Loader,
  MessageSquare, Send, Loader2
} from "lucide-react";
import SEO from "./SEO";

/* ─── API ─── */
const API_BASE = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";

const INSPECT_MODAL_RESPONSIVE_CSS = `
@media (max-width: 760px) {
  .inspect-modal-overlay {
    align-items: stretch !important;
    justify-content: flex-start !important;
    padding: max(10px, env(safe-area-inset-top)) 10px max(10px, env(safe-area-inset-bottom)) !important;
    overflow-y: auto !important;
  }
  .inspect-modal-shell {
    width: 100% !important;
    max-width: none !important;
    height: calc(100dvh - 20px) !important;
    max-height: calc(100dvh - 20px) !important;
    border-radius: 16px !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .inspect-modal-header {
    padding: 12px 14px !important;
    gap: 10px !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 58vw !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
  .inspect-modal-tabs,
  .inspect-modal-controls-row {
    width: 100% !important;
    order: 3 !important;
    justify-content: stretch !important;
  }
  .inspect-modal-tabs button,
  .inspect-modal-controls-row button {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    padding: 8px 8px !important;
    font-size: 10px !important;
  }
  .inspect-modal-body {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    overflow-y: auto !important;
    min-height: 0 !important;
  }
  .inspect-modal-image-panel {
    flex: 0 0 auto !important;
    width: 100% !important;
    min-height: 0 !important;
    height: min(54dvh, 420px) !important;
  }
  .inspect-modal-image-panel > div {
    min-height: 0 !important;
    height: 100% !important;
  }
  .inspect-modal-image-panel img {
    object-fit: contain !important;
  }
  .inspect-modal-side {
    width: 100% !important;
    max-height: none !important;
    flex: 1 1 auto !important;
    border-left: none !important;
    border-top: 1px solid rgba(200,160,101,.18) !important;
    overflow-y: visible !important;
  }
  .inspect-modal-side > div {
    padding: 14px !important;
  }
  .inspect-modal-side [style*="flex-direction:column"] {
    flex-direction: row !important;
    flex-wrap: wrap !important;
  }
  .inspect-mobile-hide {
    display: none !important;
  }
}
@media (max-width: 420px) {
  .inspect-modal-image-panel {
    height: 46dvh !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 48vw !important;
  }
}
`;
const formatMAD = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 MAD";
  return `${amount.toLocaleString("en-MA", { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} MAD`;
};


function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_BASE}/api/images/uploads/${url.split("/").pop()}`;
}

/* ─── Transform raw API product ─── */
function transformProduct(p) {
  const meta = {};
  (p.productMetadata || []).forEach(({ key, value }) => { meta[key] = value; });

  const sizeArray = meta.size
    ? meta.size.split(",").map(s => s.trim())
    : meta.sizes
      ? meta.sizes.split(",").map(s => s.trim())
      : ["One Size"];

  const colors = meta.color
    ? meta.color.split(",").map(c => c.trim())
    : [];

  /* Collect ALL images: main image first, then any extras from the API.
     Supports common backend shapes: images / productImages / gallery, which
     can be arrays of strings OR arrays of objects with {url} / {imageUrl}.
     Duplicates are removed so we never show the same picture twice.        */
  const imageList = [];
  const pushImg = (url) => {
    const resolved = resolveImg(url);
    if (resolved && !imageList.includes(resolved)) imageList.push(resolved);
  };

  pushImg(p.imageUrl);

  const extras = p.images || p.productImages || p.gallery || [];
  if (Array.isArray(extras)) {
    extras.forEach((item) => {
      if (!item) return;
      const url = typeof item === "string"
        ? item
        : (item.url || item.imageUrl || item.src || null);
      if (url) pushImg(url);
    });
  }

  return {
    id:          p.id,
    name:        p.name,
    price:       parseFloat(p.price) || 0,
    orig:        p.origin ? parseFloat(p.origin) : (meta.originalPrice ? parseFloat(meta.originalPrice) : null),
    rating:      parseFloat((p.rating ?? 0).toFixed(1)),
    reviews:     p._count?.reviews ?? p.reviews?.length ?? parseInt(meta.reviews ?? "0", 10),
    tag:         p.tag || "",
    cat:         p.category?.name || meta.gender || "Uncategorized",
    catId:       p.categoryId || p.category?.id || null,
    style:       meta.style || "",
    sizes:       sizeArray,
    colors:      colors,
    material:    meta.material || null,
    description: p.description || "",
    img:         imageList[0] || null,
    images:      imageList,
    stock:       p.stock ?? 99,
    sku:         p.sku || `SKU-${p.id}`,
    weight:      meta.weight || null,
    dimensions:  meta.dimensions || null,
    origin:      meta.origin || "Morocco",
    careGuide:   meta.careGuide || null,
  };
}

/* ─── Stars ─── */
function Stars({ n = 5, max = 5, size = 13 }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: max }).map((_, i) => (
        <Star key={i} size={size}
          fill={i < Math.floor(n) ? "#c8a065" : (i < n ? "#c8a065" : "#e2d8ca")}
          color={i < Math.ceil(n) ? "#c8a065" : "#e2d8ca"}
        />
      ))}
    </span>
  );
}

/* ─── Zoom / Lens Modal ─── */
function ZoomModal({ img, name, onClose }) {
  const { t, i18n } = useTranslation();

  const containerRef = useRef(null);
  const [zoom, setZoom]       = useState(1);
  const [pos, setPos]         = useState({ x: 50, y: 50 });
  const [isDragging, setDrag] = useState(false);
  const [lensPos, setLensPos] = useState({ x: -300, y: -300 });
  const [lensOn, setLensOn]   = useState(false);
  const [mode, setMode]       = useState("zoom");
  const dragRef = useRef({ x: 0, y: 0, px: 50, py: 50 });
  const LENS = 170;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  const handleWheel = useCallback(e => {
    e.preventDefault();
    setZoom(z => clamp(z - e.deltaY * 0.006, 1, 8));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handleMouseMove = useCallback(e => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setLensPos({ x: e.clientX - r.left, y: e.clientY - r.top });
    if (isDragging) {
      const dx = ((e.clientX - dragRef.current.x) / r.width) * 100 / zoom;
      const dy = ((e.clientY - dragRef.current.y) / r.height) * 100 / zoom;
      setPos({
        x: clamp(dragRef.current.px - dx, 0, 100),
        y: clamp(dragRef.current.py - dy, 0, 100),
      });
    }
  }, [isDragging, zoom]);

  const handleMouseDown = e => {
    if (zoom <= 1) return;
    setDrag(true);
    dragRef.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
  };

  useEffect(() => {
    const up = () => setDrag(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", up);
    };
  }, [handleMouseMove]);

  const ZOOM_LEVELS = [1, 1.5, 2, 3, 4, 6, 8];
  const zBtnStyle = {
    width: 34, height: 34, borderRadius: 8,
    background: "rgba(200,160,101,.1)", border: "1px solid rgba(200,160,101,.2)",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0,
  };

  return (
    <div className="inspect-modal-overlay" style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(8,14,12,.92)", backdropFilter: "blur(20px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      
      <style>{INSPECT_MODAL_RESPONSIVE_CSS}</style>
      <div className="inspect-modal-shell" style={{
        background: "#0f1a17", borderRadius: 24, overflow: "hidden",
        width: "min(1000px,96vw)", maxHeight: "92vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 60px 140px rgba(0,0,0,.7), 0 0 0 1px rgba(200,160,101,.25)",
        border: "1px solid rgba(200,160,101,.2)",
      }}>
        {/* Header */}
        <div className="inspect-modal-header" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 24px", borderBottom: "1px solid rgba(200,160,101,.12)",
          background: "rgba(200,160,101,.04)", flexWrap: "wrap", gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "linear-gradient(135deg,#1f3d3a,#2d5550)",
              border: "1px solid rgba(200,160,101,.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Eye size={15} color="#c8a065" />
            </div>
            <div>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".15em", color: "#c8a065" }}>{t("product_detail.detail_inspector")}</p>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "rgba(253,248,242,.5)", fontWeight: 300 }}>{name}</p>
            </div>
          </div>

          <div className="inspect-modal-controls-row" style={{ display: "flex", gap: 6 }}>
            {[{ id: "zoom", label: "ZOOM", icon: ZoomIn }, { id: "lens", label: "LENS", icon: Eye }].map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setMode(id)} style={{
                padding: "7px 14px", borderRadius: 6,
                background: mode === id ? "#c8a065" : "rgba(200,160,101,.1)",
                border: `1px solid ${mode === id ? "#c8a065" : "rgba(200,160,101,.2)"}`,
                color: mode === id ? "#1f3d3a" : "rgba(253,248,242,.6)",
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: ".1em", cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                transition: "all .2s",
              }}>
                <Icon size={12} />{label}
              </button>
            ))}
          </div>

          <button onClick={onClose} style={{
            width: 34, height: 34, borderRadius: "50%",
            background: "rgba(200,160,101,.1)", border: "1px solid rgba(200,160,101,.2)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}><X size={16} color="rgba(253,248,242,.7)" /></button>
        </div>

        {/* Viewer */}
        <div className="inspect-modal-body" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div
            className="inspect-modal-image-panel"
            ref={containerRef}
            onMouseEnter={() => setLensOn(true)}
            onMouseLeave={() => { setLensOn(false); setDrag(false); }}
            onMouseDown={handleMouseDown}
            style={{
              position: "relative", flex: 1, overflow: "hidden",
              cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : (mode === "lens" ? "none" : "crosshair"),
              background: "#0a1210", minHeight: 360,
            }}
          >
            <img
              src={img} alt={name} draggable={false}
              style={{
                width: "100%", height: "100%", objectFit: "contain", display: "block",
                transform: `scale(${zoom}) translate(${(50 - pos.x) * 0.8}%, ${(50 - pos.y) * 0.8}%)`,
                transformOrigin: `${pos.x}% ${pos.y}%`,
                transition: isDragging ? "none" : "transform .2s ease",
                userSelect: "none",
              }}
            />

            {/* Lens */}
            {mode === "lens" && lensOn && (
              <div style={{
                position: "absolute",
                left: lensPos.x - LENS / 2, top: lensPos.y - LENS / 2,
                width: LENS, height: LENS, borderRadius: "50%",
                border: "2px solid rgba(200,160,101,.8)", overflow: "hidden",
                pointerEvents: "none",
                boxShadow: "0 0 0 1px rgba(200,160,101,.3), 0 10px 30px rgba(0,0,0,.5)",
                zIndex: 10,
              }}>
                <img src={img} alt="" style={{
                  position: "absolute",
                  width: `${(containerRef.current?.offsetWidth || 600) * 3}px`,
                  height: `${(containerRef.current?.offsetHeight || 400) * 3}px`,
                  left: -(lensPos.x * 3 - LENS / 2),
                  top: -(lensPos.y * 3 - LENS / 2),
                  objectFit: "cover", userSelect: "none",
                }} />
                <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: 1, background: "rgba(200,160,101,.4)" }} />
                  <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 1, background: "rgba(200,160,101,.4)" }} />
                </div>
              </div>
            )}

            {/* Grid */}
            {mode === "zoom" && zoom > 1 && (
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                backgroundImage: "linear-gradient(rgba(200,160,101,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(200,160,101,.05) 1px,transparent 1px)",
                backgroundSize: "40px 40px",
              }} />
            )}

            {/* Zoom indicator */}
            <div style={{
              position: "absolute", bottom: 16, left: 16,
              background: "rgba(15,26,23,.85)", border: "1px solid rgba(200,160,101,.25)",
              borderRadius: 8, padding: "6px 12px",
              fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
              color: "#c8a065", backdropFilter: "blur(8px)",
            }}>
              {zoom.toFixed(1)}×
            </div>

            {mode === "zoom" && zoom <= 1 && (
              <div style={{
                position: "absolute", bottom: 16, right: 16,
                background: "rgba(15,26,23,.75)", border: "1px solid rgba(200,160,101,.2)",
                borderRadius: 8, padding: "6px 12px",
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "rgba(253,248,242,.5)",
              }}>{t("product_detail.scroll_to_zoom")}</div>
            )}
          </div>

          {/* Controls */}
          <div style={{
            padding: "12px 24px", borderTop: "1px solid rgba(200,160,101,.1)",
            background: "rgba(200,160,101,.03)",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}>
            <button onClick={() => setZoom(z => clamp(z - 0.5, 1, 8))} style={zBtnStyle}><ZoomOut size={14} color="#c8a065" /></button>

            <div style={{ flex: 1, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {ZOOM_LEVELS.map(z => (
                <button key={z} onClick={() => { setZoom(z); setPos({ x: 50, y: 50 }); }} style={{
                  padding: "5px 12px", borderRadius: 5,
                  background: Math.abs(zoom - z) < 0.1 ? "#c8a065" : "rgba(200,160,101,.1)",
                  border: `1px solid ${Math.abs(zoom - z) < 0.1 ? "#c8a065" : "rgba(200,160,101,.2)"}`,
                  color: Math.abs(zoom - z) < 0.1 ? "#1f3d3a" : "rgba(253,248,242,.6)",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                  cursor: "pointer", transition: "all .15s",
                }}>{z}×</button>
              ))}
            </div>

            <button onClick={() => setZoom(z => clamp(z + 0.5, 1, 8))} style={zBtnStyle}><ZoomIn size={14} color="#c8a065" /></button>
            <button onClick={() => { setZoom(1); setPos({ x: 50, y: 50 }); }} style={{
              ...zBtnStyle, width: "auto", padding: "0 14px",
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: ".1em", color: "rgba(253,248,242,.5)",
            }}>{t("product_detail.reset")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════ MAIN PAGE ════ */
const TAG_STYLE = {
  Bestseller: { background: "#1f3d3a", color: "#dfcc25" },
  New:        { background: "#c8a065", color: "#fff" },
  Sale:       { background: "#c0533a", color: "#fff" },
};

const TABS = ["DESCRIPTION", "SPECIFICATIONS", "REVIEWS", "CARE GUIDE"];

const TRUST = [
  { icon: "💎", title: "CERTIFIED QUALITY",  desc: "Carefully selected,\nbuilt to last." },
  { icon: "🔨", title: "HANDCRAFTED",        desc: "Artisan-made with\nprecision & care." },
  { Icon: Truck,     title: "FREE DELIVERY",  desc: "On orders over 200 MAD\nacross Morocco." },
  { Icon: RotateCcw, title: "30-DAY RETURNS", desc: "Hassle-free policy,\nno questions asked." },
];

function colorHex(name) {
  const map = {
    gold: "#c8a065", black: "#2a2a2a", silver: "#c0c0c0", white: "#f8f8f8",
    red: "#c0533a", blue: "#3a6070", green: "#1f3d3a", brown: "#8b5e3c",
    pink: "#e8a0a0", beige: "#d4c5a9", navy: "#1a2540", grey: "#8a8a8a", gray: "#8a8a8a",
  };
  return map[(name || "").toLowerCase()] || "#aaa";
}

/* ─── Interactive Star Input ─── */
function StarInput({ value, onChange, size = 24 }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div style={{ display: "inline-flex", gap: 4 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 2,
            transition: "transform .15s",
            transform: hover === i ? "scale(1.15)" : "scale(1)",
          }}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          <Star
            size={size}
            fill={i <= display ? "#c8a065" : "transparent"}
            color={i <= display ? "#c8a065" : "#d4c5b0"}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

/* ─── Reviews Section (fetches + displays + submits) ─── */
function ReviewsSection({ productId }) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // auth detection — adjust the key if yours differs
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        "")) ||
    "";
  const currentUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/reviews`);
      if (!res.ok) throw new Error("Failed to load reviews");
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
      // if the user already reviewed, pre-fill their existing review
      if (currentUser) {
        const mine = data.find((r) => r.userId === currentUser.id);
        if (mine) {
          setRating(mine.rating);
          setComment(mine.comment || "");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [productId, currentUser?.id]);

  useEffect(() => {
    if (productId) loadReviews();
  }, [productId, loadReviews]);

  const userAlreadyReviewed =
    currentUser && reviews.some((r) => r.userId === currentUser.id);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitError(null);
    setSubmitSuccess(false);
    if (!rating) { setSubmitError(t("reviews.error_rating", "Please pick a rating")); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ productId, rating, comment: comment.trim() || undefined }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.error || payload.message || `HTTP ${res.status}`);
      }
      setSubmitSuccess(true);
      setComment("");
      setRating(0);
      // refresh the list so the new review appears
      loadReviews();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const initials = (name) =>
    (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div style={{ padding: "20px 18px", borderTop: "1px solid rgba(200,160,101,.14)" }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquare size={14} color="#1f3d3a" />
          <p style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: ".12em", color: "#1f3d3a", margin: 0,
          }}>
            {t("reviews.title", "REVIEWS")}
          </p>
        </div>
        {reviews.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Stars n={Math.round(avg)} size={12} />
            <span style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              color: "#7a6a58", letterSpacing: ".05em",
            }}>
              {avg} · {reviews.length}
            </span>
          </div>
        )}
      </div>

      {/* List */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 20, color: "#b0a090" }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
        </div>
      )}
      {error && (
        <div style={{
          padding: "10px 12px", borderRadius: 8, background: "#fef2f2", color: "#991b1b",
          fontSize: 12, fontFamily: "'Montserrat',sans-serif",
        }}>
          {error}
        </div>
      )}

      {!loading && !error && reviews.length === 0 && (
        <div style={{
          padding: "18px 16px", textAlign: "center", borderRadius: 10,
          background: "rgba(200,160,101,.06)",
          fontFamily: "'Cormorant Garamond',serif", fontSize: 14, color: "#7a6a58", fontStyle: "italic",
        }}>
          {t("reviews.empty", "No reviews yet. Be the first to share your thoughts.")}
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
          {reviews.map((r) => (
            <div key={r.id} style={{
              padding: "12px 14px", borderRadius: 10,
              background: "#fff", border: "1px solid rgba(200,160,101,.18)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "linear-gradient(135deg,#1f3d3a,#2d5550)",
                  color: "#dfcc25",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                }}>
                  {initials(r.user?.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond',serif", fontSize: 14, fontWeight: 600,
                    color: "#1f3d3a", lineHeight: 1.2,
                  }}>
                    {r.user?.name || t("reviews.anonymous", "Anonymous")}
                  </div>
                  <div style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#b0a090",
                    letterSpacing: ".05em",
                  }}>
                    {formatDate(r.createdAt)}
                  </div>
                </div>
                <Stars n={Math.round(r.rating)} size={11} />
              </div>
              {r.comment && (
                <p style={{
                  margin: 0, fontFamily: "'Cormorant Garamond',serif",
                  fontSize: 14, lineHeight: 1.55, color: "#4a3d2f",
                }}>
                  {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Write form */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px dashed rgba(200,160,101,.25)" }}>
        {!token && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(31,61,58,.05)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#1f3d3a", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.login_required", "Please log in to write a review.")}
          </p>
        )}

        {token && userAlreadyReviewed && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(200,160,101,.08)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#7a6a58", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.already_reviewed", "You've already reviewed this product. Thank you!")}
          </p>
        )}

        {token && !userAlreadyReviewed && (
          <form onSubmit={submit}>
            <p style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: ".12em", color: "#1f3d3a", margin: "0 0 10px",
            }}>
              {t("reviews.write_title", "SHARE YOUR EXPERIENCE")}
            </p>

            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".1em",
              }}>
                {t("reviews.your_rating", "YOUR RATING")}
              </span>
              <StarInput value={rating} onChange={setRating} size={22} />
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("reviews.placeholder", "Tell others what you think about this piece…")}
              rows={3}
              maxLength={1000}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "10px 12px", borderRadius: 10,
                border: "1px solid rgba(200,160,101,.35)",
                background: "#fdfaf5", outline: "none", resize: "vertical",
                fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
                color: "#1f3d3a", lineHeight: 1.5,
              }}
              onFocus={e => e.currentTarget.style.borderColor = "#1f3d3a"}
              onBlur={e => e.currentTarget.style.borderColor = "rgba(200,160,101,.35)"}
            />

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 10, gap: 10, flexWrap: "wrap",
            }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".05em",
              }}>
                {comment.length}/1000
              </span>
              <button
                type="submit"
                disabled={submitting || !rating}
                style={{
                  padding: "10px 18px", borderRadius: 10,
                  background: submitting || !rating ? "#9ca3a0" : "#1f3d3a",
                  border: "none",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
                  letterSpacing: ".1em", color: "#fdf8f2",
                  cursor: submitting || !rating ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transition: "background .2s",
                }}
                onMouseEnter={e => { if (!submitting && rating) e.currentTarget.style.background = "#2d5550"; }}
                onMouseLeave={e => { if (!submitting && rating) e.currentTarget.style.background = "#1f3d3a"; }}
              >
                {submitting
                  ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                  : <Send size={13} />}
                {t("reviews.submit", "POST REVIEW")}
              </button>
            </div>

            {submitError && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#fef2f2", color: "#991b1b",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {submitError}
              </p>
            )}
            {submitSuccess && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#f0fdf4", color: "#166534",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {t("reviews.success", "Thank you for your review!")}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}


export default function ProductDetails() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [product,      setProduct]      = useState(null);
  const [related,      setRelated]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [activeImg,    setActiveImg]    = useState(0);
  const [activeColor,  setActiveColor]  = useState(0);
  const [activeSize,   setActiveSize]   = useState(null);
  const [qty,          setQty]          = useState(1);
  const [activeTab,    setActiveTab]    = useState(0);
  const [wishlist,     setWishlist]     = useState(false);
  const [cartAdded,    setCartAdded]    = useState(false);
  const [zoomModal,    setZoomModal]    = useState(false);
  const [imgLoaded,    setImgLoaded]    = useState(false);

  const productSeo = product
  ? {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description:
        product.description ||
        `${product.name} available at Dar Oulmès Store in Oulmès.`,
      image: product.img ? [product.img] : [],
      brand: {
        "@type": "Brand",
        name: "Dar Oulmès",
      },
      category: product.cat || "Local Products",
      sku: product.sku || `DAR-${product.id}`,
      aggregateRating:
        product.reviews > 0
          ? {
              "@type": "AggregateRating",
              ratingValue: product.rating || 5,
              reviewCount: product.reviews,
            }
          : undefined,
      offers: {
        "@type": "Offer",
        url: `https://daroulmes.store/products/${product.id}`,
        priceCurrency: "MAD",
        price: Number(product.price || 0).toFixed(2),
        availability:
          product.stock > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
      },
    }
  : null;
  
  /* Fetch product + related */
  useEffect(() => {
    if (!id) return;
    setLoading(true); setError(null); setActiveImg(0); setImgLoaded(false);

    fetch(`${API_BASE}/api/products/${id}`)
      .then(r => { if (!r.ok) throw new Error("Product not found"); return r.json(); })
      .then(data => {
        const p = transformProduct(data);
        setProduct(p);
        if (p.sizes?.length) setActiveSize(p.sizes[0]);
        if (p.catId) {
          return fetch(`${API_BASE}/api/products?categoryId=${p.catId}&limit=8`)
            .then(r => r.json()).catch(() => []);
        }
        return [];
      })
      .then(relData => {
        const list = relData?.products || relData?.data || (Array.isArray(relData) ? relData : []);
        setRelated(list.map(transformProduct).filter(p => String(p.id) !== String(id)).slice(0, 4));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  /* Wishlist */
  useEffect(() => {
    try {
      const wl = JSON.parse(localStorage.getItem("dar_oulmes_wishlist") || "[]");
      setWishlist(wl.includes(parseInt(id)));
    } catch {}
  }, [id]);

  const toggleWish = () => {
    try {
      const wl = JSON.parse(localStorage.getItem("dar_oulmes_wishlist") || "[]");
      const nw = wishlist ? wl.filter(x => x !== parseInt(id)) : [...wl, parseInt(id)];
      localStorage.setItem("dar_oulmes_wishlist", JSON.stringify(nw));
      setWishlist(!wishlist);
    } catch {}
  };

  /* Add to cart */
  const addToCart = useCallback(() => {
    if (!product) return;
    try {
      const stored = localStorage.getItem("dar_oulmes_cart");
      const cart   = stored ? JSON.parse(stored) : [];
      const exists = cart.find(c => c.id === product.id);
      if (exists) exists.quantity += qty;
      else cart.push({
        id: product.id, name: product.name, price: product.price,
        img: product.img, tag: product.tag, quantity: qty,
        size: activeSize, color: product.colors[activeColor] || null,
      });
      localStorage.setItem("dar_oulmes_cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cart-updated"));
      setCartAdded(true);
      setTimeout(() => setCartAdded(false), 1600);
    } catch {}
  }, [product, qty, activeSize, activeColor]);

  const images = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    return product.img ? [product.img] : [];
  }, [product]);

  const discount = product?.orig
    ? Math.round((1 - product.price / product.orig) * 100)
    : null;

  /* Loading */
  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#fdf8f2", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 18 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#1f3d3a,#2d5550)", display: "flex", alignItems: "center", justifyContent: "center", animation: "spin 1.2s linear infinite" }}>
        <Sparkles size={22} color="#c8a065" />
      </div>
      <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#9a8a78", letterSpacing: ".1em" }}>{t("product_detail.loading_product")}</p>
    </div>
  );

  /* Error */
  if (error) return (
    <div style={{ minHeight: "100vh", background: "#fdf8f2", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 18 }}>
      <X size={36} color="#c0533a" />
      <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: "#c0533a" }}>{error}</p>
      <button onClick={() => navigate(-1)} style={backBtn}>← {t("product_detail.go_back")}</button>
    </div>
  );

  if (!product) return null;

  return (
    <div style={{ fontFamily: "'Montserrat',sans-serif",paddingTop:"50px", background: "#fdf8f2", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{CSS}</style>
      {product && (
  <SEO
    title={`${product.name} — Dar Oulmès Store`}
    description={
      product.description ||
      `Buy ${product.name} from Dar Oulmès Store. Local product available in Oulmès with easy WhatsApp ordering.`
    }
    canonical={`/products/${product.id}`}
    image={product.img || "/og-image.jpg"}
    type="product"
    jsonLd={productSeo}
  />
)}
      {/* Decorative bg */}
      <div className="pd-deco" aria-hidden="true">
        <svg width="100%" height="100%" viewBox="0 0 900 1800" preserveAspectRatio="xMinYMin slice">
          {[0,36,72,108,144,180].map((r, i) => (
            <line key={`l${i}`} x1="0" y1="0" x2={500*Math.cos(r*Math.PI/180)} y2={500*Math.sin(r*Math.PI/180)} stroke="#c8a065" strokeWidth=".6" strokeOpacity=".2" />
          ))}
          {[0,36,72,108,144,180].map((r, i) => (
            <line key={`r${i}`} x1="900" y1="1800" x2={900-500*Math.cos(r*Math.PI/180)} y2={1800-500*Math.sin(r*Math.PI/180)} stroke="#c8a065" strokeWidth=".6" strokeOpacity=".2" />
          ))}
          {[[830,300],[870,420],[800,180],[55,1600],[25,1720],[90,1660]].map(([cx,cy],i) => (
            <circle key={`d${i}`} cx={cx} cy={cy} r="2.5" fill="#c8a065" opacity=".25" />
          ))}
        </svg>
      </div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", padding: "0 28px 100px" }}>

        {/* Breadcrumb */}
        <div className="pd-fu pd-fu1" style={{
          display: "flex", alignItems: "center", gap: 8, padding: "30px 0 26px",
          fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78",
        }}>
          <button onClick={() => navigate(-1)} style={backBtn}><ArrowLeft size={13} /> {t("product_detail.back")}</button>
          <ChevronRight size={11} color="#ccc" />
          {["Home", product.cat, product.name].map((c, i, a) => (
            <React.Fragment key={`${c}-${i}`}>
              <span style={{ color: i === a.length - 1 ? "#1f3d3a" : "#9a8a78", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c}</span>
              {i < a.length - 1 && <ChevronRight size={11} color="#ccc" />}
            </React.Fragment>
          ))}
        </div>

        {/* ══ TOP GRID ══ */}
        <div className="pd-fu pd-fu2 pd-top-grid">

          {/* LEFT – Gallery */}
          <div>
            {/* Main image */}
            <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", background: "#f0e8de", marginBottom: 14, aspectRatio: "4/5" }}>
              {!imgLoaded && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#f0e8de,#e8ddd0)" }}>
                  <Loader size={24} color="#c8a065" style={{ animation: "spin 1s linear infinite" }} />
                </div>
              )}
              <img
                src={images[activeImg]} alt={product.name}
                onLoad={() => setImgLoaded(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: imgLoaded ? 1 : 0, transition: "opacity .4s" }}
              />

              {/* Tag */}
              {product.tag && TAG_STYLE[product.tag] && (
                <span style={{ position: "absolute", top: 14, left: 14, ...TAG_STYLE[product.tag], fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".1em", padding: "5px 12px", borderRadius: 4 }}>
                  {product.tag}
                </span>
              )}

              {/* Discount */}
              {discount && (
                <span style={{ position: "absolute", top: 14, right: 14, background: "#c0533a", color: "#fff", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, padding: "5px 11px", borderRadius: 4 }}>
                  −{discount}%
                </span>
              )}

              {/* Nav arrows */}
              {images.length > 1 && [
                { Icon: ChevronLeft,  fn: () => { setActiveImg(i => (i-1+images.length)%images.length); setImgLoaded(false); }, side: "left" },
                { Icon: ChevronRight, fn: () => { setActiveImg(i => (i+1)%images.length); setImgLoaded(false); }, side: "right" },
              ].map(({ Icon, fn, side }) => (
                <button key={side} onClick={fn} className="pd-arrow" style={{ [side]: 12 }}>
                  <Icon size={16} color="#1f3d3a" />
                </button>
              ))}

              {/* Action buttons */}
              <div style={{ position: "absolute", bottom: 14, right: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={() => setZoomModal(true)} className="pd-action-btn" title={t("product_detail.inspect_detail")}><ZoomIn size={16} color="#1f3d3a" /></button>
                <button onClick={toggleWish} className="pd-action-btn" title={t("product_detail.wishlist")}>
                  <Heart size={16} fill={wishlist ? "#c0533a" : "none"} color={wishlist ? "#c0533a" : "#1f3d3a"} />
                </button>
              </div>

              {/* Counter */}
              {images.length > 1 && (
                <div style={{ position: "absolute", bottom: 14, left: 14, background: "rgba(255,253,249,.88)", backdropFilter: "blur(6px)", border: "1px solid rgba(200,160,101,.3)", borderRadius: 20, padding: "4px 11px", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 600, color: "#1f3d3a" }}>
                  {activeImg + 1} / {images.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(images.length, 4)},1fr)`, gap: 10, marginBottom: 14 }}>
                {images.map((img, i) => (
                  <div key={i} className={`pd-thumb${i === activeImg ? " active" : ""}`} style={{ aspectRatio: "1/1" }}
                    onClick={() => { setActiveImg(i); setImgLoaded(false); }}>
                    <img src={img} alt={`View ${i+1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                ))}
              </div>
            )}

            {/* Gem inspector callout */}
            <div className="pd-inspect-callout" onClick={() => setZoomModal(true)}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>💎</span>
                <div>
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: "#1f3d3a", marginBottom: 2 }}>{t("product_detail.detail_inspector")}</p>
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 300, color: "#7a6a58" }}>{t("product_detail.click_to_examine")}</p>
                </div>
              </div>
              <ZoomIn size={18} color="#c8a065" />
            </div>
          </div>

          {/* RIGHT – Info */}
          <div style={{ display: "flex", flexDirection: "column" }}>

            {/* Category / SKU */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: ".12em", color: "#c8a065" }}>
                {product.material ? `${product.material} · ` : ""}{product.cat.toUpperCase()}
              </span>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#bbb", letterSpacing: ".08em" }}>#{product.sku}</span>
            </div>

            {/* Name */}
            <h1 style={{ fontFamily:  "'Montserrat',sans-serif", fontSize: "clamp(26px,3.5vw,42px)", fontWeight: 600, color: "#1f3d3a", lineHeight: 1.2, marginBottom: 16 }}>
              {product.name}
            </h1>

            {/* Rating + stock */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <Stars n={product.rating} />
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#7a6a58" }}>
                {product.rating > 0 ? product.rating.toFixed(1) : "—"} ({product.reviews} review{product.reviews !== 1 ? "s" : ""})
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: product.stock > 0 ? "#2a8a4a" : "#c0533a", fontWeight: 500 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: product.stock > 0 ? "#2a8a4a" : "#c0533a", display: "inline-block" }} />
                {product.stock > 0 ? `In Stock (${product.stock})` : "Out of Stock"}
              </span>
            </div>

            {/* Price */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 18 }}>
              <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 44, fontWeight: 600, color: "#1f3d3a", lineHeight: 1 }}>
                {formatMAD(product.price)}
              </span>
              {product.orig && (
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: "#b0a090", textDecoration: "line-through" }}>
                  {formatMAD(product.orig)}
                </span>
              )}
              {discount && (
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, background: "rgba(192,83,58,.1)", color: "#c0533a", padding: "3px 8px", borderRadius: 4, letterSpacing: ".06em" }}>
                  SAVE {discount}%
                </span>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#7a6a58", lineHeight: 1.85, marginBottom: 20 }}>
                {product.description}
              </p>
            )}

            <hr style={{ border: "none", borderTop: "1px solid rgba(200,160,101,.2)", marginBottom: 20 }} />

            {/* Colors */}
            {product.colors.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 500, color: "#1f3d3a", letterSpacing: ".06em", marginBottom: 10 }}>
                  {t("product_detail.color")} — <span style={{ fontWeight: 300, color: "#9a8a78" }}>{product.colors[activeColor]}</span>
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {product.colors.map((c, i) => (
                    <button key={c} onClick={() => setActiveColor(i)}
                      className={`pd-color-swatch${i === activeColor ? " active" : ""}`}
                      title={c} style={{ background: colorHex(c) }} />
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.sizes.length > 0 && product.sizes[0] !== "One Size" && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 500, color: "#1f3d3a", letterSpacing: ".06em", marginBottom: 10 }}>
                  {t("product_detail.size")} — <span style={{ fontWeight: 300, color: "#9a8a78" }}>{activeSize}</span>
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {product.sizes.map(s => (
                    <button key={s} onClick={() => setActiveSize(s)}
                      className={`pd-size-btn${activeSize === s ? " active" : ""}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 500, color: "#1f3d3a", letterSpacing: ".06em", marginBottom: 10 }}>{t("product_detail.quantity")}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button className="pd-qty-btn" onClick={() => setQty(q => Math.max(1, q-1))}><Minus size={13} color="#1f3d3a" /></button>
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 600, color: "#1f3d3a", minWidth: 26, textAlign: "center" }}>{qty}</span>
                <button className="pd-qty-btn" onClick={() => setQty(q => Math.min(product.stock, q+1))}><Plus size={13} color="#1f3d3a" /></button>
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78", fontWeight: 300 }}>
                  {t("product_detail.subtotal")}: <strong style={{ color: "#1f3d3a" }}>{formatMAD(product.price * qty)}</strong>
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
              <button className={`pd-btn-cart${cartAdded ? " done" : ""}`} onClick={addToCart} disabled={product.stock === 0}>
                {cartAdded ? <><span style={{ fontSize: 16 }}>✓</span> {t("product_detail.added_to_cart")}</> : <><ShoppingCart size={16} /> {t("product_detail.add_to_cart")}</>}
              </button>
              <button className="pd-btn-buy">BUY NOW — {formatMAD(product.price * qty)}</button>
            </div>

            {/* Trust micro */}
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {[
                { Icon: Truck,     text: "Free Shipping on orders over 200 MAD" },
                { Icon: RotateCcw, text: "30-Day Hassle-Free Returns" },
                { Icon: Shield,    text: "Secure Payment & Data Protection" },
              ].map(({ Icon, text }) => (
                <div key={text} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#7a6a58", fontWeight: 300 }}>
                  <Icon size={14} color="#c8a065" />{text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ TABS ══ */}
        <div className="pd-fu pd-fu3 pd-section">
          <div style={{ display: "flex", borderBottom: "1px solid rgba(200,160,101,.2)", marginBottom: 28, overflowX: "auto" }}>
            {TABS.map((tab, i) => (
              <button key={tab} className={`pd-tab${i === activeTab ? " active" : ""}`} onClick={() => setActiveTab(i)}>{tab}</button>
            ))}
          </div>

          {activeTab === 0 && (
            <div className="pd-tab-grid">
              <div>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#7a6a58", lineHeight: 1.9, marginBottom: 20 }}>
                  {product.description || "This product is a testament to skilled craftsmanship and premium materials. Every detail has been carefully considered to deliver both beauty and functionality."}
                </p>
                {["Premium quality materials, built to last", "Handcrafted by skilled artisans", "Meticulous attention to detail and finish"].map(txt => (
                  <div key={txt} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", border: "1.5px solid #c8a065", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <Shield size={9} color="#c8a065" />
                    </span>
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#5a4a38", lineHeight: 1.6 }}>{txt}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: "#fdf8f2", borderRadius: 10, border: "1px solid rgba(200,160,101,.2)", overflow: "hidden" }}>
                {[["Category", product.cat], ["Material", product.material || "—"], ["Sizes", product.sizes.join(", ")], ["Origin", product.origin], ["Weight", product.weight || "—"], ["SKU", product.sku]]
                  .map(([label, value], i, a) => (
                    <div key={label} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "12px 18px", borderBottom: i < a.length-1 ? "1px solid rgba(200,160,101,.12)" : "none", background: i%2===0 ? "rgba(200,160,101,.04)" : "transparent" }}>
                      <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 500, color: "#1f3d3a" }}>{label}</span>
                      <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 300, color: "#7a6a58" }}>{value}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div style={{ background: "#fdf8f2", borderRadius: 10, border: "1px solid rgba(200,160,101,.2)", overflow: "hidden" }}>
              {[["Product Name", product.name], ["Category", product.cat], ["Material", product.material || "—"], ["Available Sizes", product.sizes.join(", ")], ["Colors", product.colors.join(", ") || "—"], ["Origin", product.origin], ["Weight", product.weight || "—"], ["Dimensions", product.dimensions || "—"], ["SKU", product.sku], ["Stock", `${product.stock} units`]]
                .map(([label, value], i, a) => (
                  <div key={label} style={{ display: "grid", gridTemplateColumns: "200px 1fr", padding: "14px 20px", borderBottom: i < a.length-1 ? "1px solid rgba(200,160,101,.12)" : "none", background: i%2===0 ? "rgba(200,160,101,.04)" : "transparent" }}>
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, color: "#1f3d3a", letterSpacing: ".04em" }}>{label}</span>
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 300, color: "#7a6a58" }}>{value}</span>
                  </div>
                ))}
            </div>
          )}

          {activeTab === 2 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 32, marginBottom: 28, flexWrap: "wrap" }}>
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 60, fontWeight: 600, color: "#1f3d3a", lineHeight: 1 }}>
                    {product.rating > 0 ? product.rating.toFixed(1) : "—"}
                  </p>
                  <Stars n={product.rating} size={16} />
                  <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78", marginTop: 6 }}>
                    {product.reviews} review{product.reviews !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  {[5,4,3,2,1].map(star => {
                    const pct = product.reviews > 0 ? (star === Math.round(product.rating) ? 65 : star === Math.floor(product.rating) ? 20 : 5) : 0;
                    return (
                      <div key={star} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#7a6a58", width: 14, textAlign: "right" }}>{star}</span>
                        <Star size={11} fill="#c8a065" color="#c8a065" />
                        <div style={{ flex: 1, height: 6, background: "#f0e8de", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: "#c8a065", borderRadius: 3, transition: "width .6s ease" }} />
                        </div>
                        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78", width: 30 }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#9a8a78", fontStyle: "italic" }}>
                {product.reviews === 0 ? "No reviews yet. Be the first to review this product!" : ""}
              </p>

              {/* Live reviews list + form */}
              <div style={{ marginTop: 12, marginLeft: -18, marginRight: -18 }}>
                <ReviewsSection productId={product.id} />
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#7a6a58", lineHeight: 1.85, marginBottom: 20 }}>
                {product.careGuide || "To maintain the quality and beauty of your product, please follow these care instructions:"}
              </p>
              {["Store in a cool, dry place away from direct sunlight.", "Clean with a soft, dry cloth.", "Avoid contact with harsh chemicals or abrasives.", "Handle with care to preserve fine details and finishes."].map(tip => (
                <div key={tip} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                  <span style={{ color: "#c8a065", fontSize: 18, lineHeight: 1.2 }}>✦</span>
                  <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 300, color: "#5a4a38", lineHeight: 1.6 }}>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ══ TRUST STRIP ══ */}
        <div className="pd-fu pd-fu4 pd-trust-grid">
          {TRUST.map(({ icon, Icon, title, desc }, i) => (
            <div key={title} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "24px 20px", borderRight: i < TRUST.length-1 ? "1px solid rgba(200,160,101,.18)" : "none" }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", border: "1.5px solid rgba(200,160,101,.45)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: icon ? 20 : undefined }}>
                {icon ? icon : <Icon size={18} color="#c8a065" />}
              </div>
              <div>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".12em", color: "#1f3d3a", marginBottom: 4 }}>{title}</p>
                <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 300, color: "#7a6a58", lineHeight: 1.7, whiteSpace: "pre-line", margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ══ RELATED ══ */}
        {related.length > 0 && (
          <div className="pd-fu" style={{ marginBottom: 52 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 500, color: "#1f3d3a" }}>{t("product_detail.you_may_like")}</h2>
              <button onClick={() => navigate(-1)} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 500, color: "#c8a065", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, letterSpacing: ".06em" }}>
                {t("product_detail.view_all")} <ArrowRight size={13} />
              </button>
            </div>
            <div className="pd-related-grid">
              {related.map(p => (
                <div key={p.id} className="pd-rel-card" onClick={() => navigate(`/product/${p.id}`)}>
                  <div style={{ aspectRatio: "1/1", overflow: "hidden", background: "#f0e8de" }}>
                    <img src={p.img} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform .5s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.07)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
                  </div>
                  <div style={{ padding: "14px 14px 12px" }}>
                    {p.material && <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#c8a065", fontWeight: 500 }}>{p.material}</span>}
                    <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 500, color: "#1f3d3a", margin: "4px 0 5px", lineHeight: 1.4 }}>{p.name}</h4>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontWeight: 500, color: "#1f3d3a" }}>{formatMAD(p.price)}</p>
                      <Stars n={p.rating} size={11} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ BOTTOM BANNER ══ */}
        <div style={{ background: "#1f3d3a", borderRadius: 16, padding: "36px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", border: "1.5px solid rgba(200,160,101,.5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 28 }}>🏠</div>
            <div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 500, color: "#fdf8f2", marginBottom: 8 }}>{t("product_detail.banner_title")}</h3>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 300, color: "rgba(253,248,242,.65)", lineHeight: 1.8, maxWidth: 380, margin: 0 }}>
                {t("product_detail.banner_sub")}
              </p>
            </div>
          </div>
          <button className="pd-explore-btn" onClick={() => navigate(-1)}>
            {t("product_detail.explore_collection")} <ArrowRight size={14} />
          </button>
        </div>

      </div>

      {/* Zoom Modal */}
      {zoomModal && images[activeImg] && (
        <ZoomModal img={images[activeImg]} name={product.name} onClose={() => setZoomModal(false)} />
      )}
    </div>
  );
}

/* ─── Styles ─── */
const backBtn = {
  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 500,
  color: "#9a8a78", background: "none", border: "1px solid rgba(200,160,101,.25)",
  borderRadius: 6, padding: "5px 12px", cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 5, letterSpacing: ".04em",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }

  .pd-fu  { animation: fadeUp .7s ease both; }
  .pd-fu1 { animation-delay: .05s; }
  .pd-fu2 { animation-delay: .12s; }
  .pd-fu3 { animation-delay: .22s; }
  .pd-fu4 { animation-delay: .32s; }

  .pd-deco { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }

  .pd-top-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    margin-bottom: 36px;
    align-items: start;
  }

  /* Gallery nav arrows */
  .pd-arrow {
    position: absolute; top: 50%; transform: translateY(-50%);
    width: 38px; height: 38px; border-radius: 50%;
    background: rgba(255,253,249,.88); border: 1px solid rgba(200,160,101,.3);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,.1);
    transition: background .2s, transform .2s;
  }
  .pd-arrow:hover { background: rgba(255,253,249,1); transform: translateY(-50%) scale(1.06); }

  /* Overlay action buttons */
  .pd-action-btn {
    width: 38px; height: 38px; border-radius: 50%;
    background: rgba(255,253,249,.88); border: 1px solid rgba(200,160,101,.3);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,.1);
    transition: background .2s, transform .2s;
  }
  .pd-action-btn:hover { background: #fdf8f2; transform: scale(1.08); }

  /* Thumbnails */
  .pd-thumb {
    border: 2px solid transparent; border-radius: 10px; overflow: hidden;
    cursor: pointer; transition: border-color .25s;
  }
  .pd-thumb.active { border-color: #c8a065; }
  .pd-thumb:hover  { border-color: rgba(200,160,101,.55); }

  /* Gem inspector callout */
  .pd-inspect-callout {
    padding: 14px 18px;
    background: linear-gradient(135deg,rgba(31,61,58,.07),rgba(200,160,101,.08));
    border: 1px solid rgba(200,160,101,.28); border-radius: 12px;
    display: flex; align-items: center; justify-content: space-between;
    cursor: pointer; transition: background .2s, border-color .2s;
  }
  .pd-inspect-callout:hover {
    background: linear-gradient(135deg,rgba(31,61,58,.12),rgba(200,160,101,.13));
    border-color: rgba(200,160,101,.5);
  }

  /* Color swatch */
  .pd-color-swatch {
    width: 32px; height: 32px; border-radius: 50%;
    border: 3px solid transparent; cursor: pointer;
    transition: border-color .2s, transform .2s;
    box-shadow: 0 2px 6px rgba(0,0,0,.18);
  }
  .pd-color-swatch.active { border-color: #1f3d3a; transform: scale(1.15); }
  .pd-color-swatch:hover  { transform: scale(1.08); }

  /* Size buttons */
  .pd-size-btn {
    padding: 7px 16px; border-radius: 6px;
    border: 1.5px solid rgba(200,160,101,.3);
    background: transparent; cursor: pointer;
    font-family: 'Montserrat',sans-serif; font-size: 12px; font-weight: 500;
    color: #5a4a38; letter-spacing: .06em; transition: all .2s;
  }
  .pd-size-btn.active { background: #1f3d3a; border-color: #1f3d3a; color: #fdf8f2; }
  .pd-size-btn:hover:not(.active) { border-color: #c8a065; color: #1f3d3a; }

  /* Quantity */
  .pd-qty-btn {
    width: 34px; height: 34px; border-radius: 50%;
    border: 1.5px solid #d5c9b8; background: transparent;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: background .2s;
  }
  .pd-qty-btn:hover { background: #f0e8de; }

  /* CTA */
  .pd-btn-cart {
    width: 100%; padding: 15px; border-radius: 8px;
    background: #1f3d3a; border: none; color: #fdf8f2;
    font-family: 'Montserrat',sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: .1em; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 10px;
    transition: background .3s, transform .2s;
  }
  .pd-btn-cart:hover:not(:disabled) { background: #2d5550; transform: translateY(-1px); }
  .pd-btn-cart.done  { background: #2a7a4a; }
  .pd-btn-cart:disabled { opacity: .5; cursor: not-allowed; }

  .pd-btn-buy {
    width: 100%; padding: 15px; border-radius: 8px;
    background: transparent; border: 1.5px solid #1f3d3a; color: #1f3d3a;
    font-family: 'Montserrat',sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: .1em; cursor: pointer; transition: background .3s, color .3s;
  }
  .pd-btn-buy:hover { background: #1f3d3a; color: #fdf8f2; }

  /* Tabs section */
  .pd-section {
    background: rgba(255,253,249,.92); border: 1px solid rgba(200,160,101,.15);
    border-radius: 16px; padding: 0 28px 32px; margin-bottom: 28px;
    box-shadow: 0 6px 30px rgba(180,140,100,.07);
  }
  .pd-tab {
    font-family: 'Montserrat',sans-serif; font-size: 11px; font-weight: 600;
    letter-spacing: .1em; padding: 16px 20px; border: none; background: transparent;
    cursor: pointer; color: #9a8a78; border-bottom: 2px solid transparent;
    transition: color .2s, border-color .2s; white-space: nowrap;
  }
  .pd-tab.active { color: #1f3d3a; border-bottom-color: #c8a065; }
  .pd-tab:hover  { color: #1f3d3a; }

  .pd-tab-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }

  /* Trust grid */
  .pd-trust-grid {
    display: grid; grid-template-columns: repeat(4,1fr);
    background: rgba(255,253,249,.92); border: 1px solid rgba(200,160,101,.15);
    border-radius: 16px; overflow: hidden; margin-bottom: 40px;
    box-shadow: 0 6px 30px rgba(180,140,100,.07);
  }

  /* Related */
  .pd-related-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
  .pd-rel-card {
    border-radius: 14px; overflow: hidden; background: #fff;
    border: 1px solid rgba(200,160,101,.15); box-shadow: 0 6px 22px rgba(180,140,100,.07);
    transition: transform .35s, box-shadow .35s; cursor: pointer;
  }
  .pd-rel-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(180,140,100,.16); }

  /* Explore btn */
  .pd-explore-btn {
    flex-shrink: 0; padding: 14px 28px; border-radius: 6px;
    background: #c8a065; border: none;
    font-family: 'Montserrat',sans-serif; font-size: 11px; font-weight: 700;
    letter-spacing: .12em; color: #1f3d3a; cursor: pointer;
    display: flex; align-items: center; gap: 8px; white-space: nowrap;
    transition: background .3s, transform .2s;
    box-shadow: 0 6px 20px rgba(200,160,101,.4);
  }
  .pd-explore-btn:hover { background: #d4ae78; transform: translateY(-1px); }

  @media (max-width: 800px) {
    .pd-top-grid       { grid-template-columns: 1fr; gap: 24px; }
    .pd-related-grid   { grid-template-columns: repeat(2,1fr); }
    .pd-trust-grid     { grid-template-columns: repeat(2,1fr); }
    .pd-tab-grid       { grid-template-columns: 1fr; }
  }
  @media (max-width: 480px) {
    .pd-related-grid   { grid-template-columns: 1fr; }
    .pd-trust-grid     { grid-template-columns: 1fr; }
  }
`;